<template>
  <!-- card -->
  <div
    @click="handleCardClick"
    @click.middle="handleCardClick"
    @keyup.enter="handleCardEnter"
    class="cit-card position-relative d-flex"
    :class="{ 'cit-lazy': bgImageLazy, cardClass }"
    :style="cardStyle"
    :tabindex="cardTabIndex">
    <img
      v-if="dataBgImage"
      :src="dataBgImage"
      alt="background-image"
      class="card-img position-absolute"
      :class="{ 'cit-lazy': bgImageLazy }" />
    <!-- title and cta -->
    <div
      :style="gradientStyle"
      class="cit-front position-absolute w-100 d-inline-flex flex-column justify-content-end p-space-sm p-lg-space-md">
      <h3 :class="titleClass" class="mb-space-xs mb-lg-0">{{ title }}</h3>
      <div
        v-if="displayCta"
        :class="ctaParentClass"
        class="d-inline-flex d-lg-none">
        <a
          @click="handleCtaClick"
          :href="ctaLink"
          :class="ctaMobileClass"
          :target="ctaLinkTarget"
          class="fw-bold mb-0"
          :aria-label="ctaAriaLabel"
          >{{ ctaText }}</a
        >
      </div>
    </div>

    <!-- on-hover tile desktop -->
    <div
      class="cit-tile position-relative d-none d-lg-inline-flex flex-column justify-content-center m-space-sm p-space-sm bg-white w-100">
      <div v-html="text" :class="textClass" class="fw-bold mb-space-sm"></div>
      <div v-if="displayCta" :class="ctaParentClass" class="d-inline-flex">
        <a
          @click="handleCtaClick"
          :href="ctaLink"
          :class="ctaDesktopClass"
          :target="ctaLinkTarget"
          class="fw-bold mb-0"
          :aria-label="ctaAriaLabel"
          >{{ ctaText }}</a
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted, type StyleValue } from "vue";

interface Props {
  bgImageSrc?: string;
  bgImageLazy?: boolean;
  displayGradient?: boolean;
  gradientOpacity?: number;
  clickableCard?: boolean;
  title?: string;
  titleSize?: string;
  titleVariant?: string;
  titleAlign?: "left" | "center" | "right";
  text?: string;
  textSize?: string;
  textVariant?: string;
  textAlign?: "left" | "center" | "right";
  displayCta?: boolean;
  ctaLink?: string;
  ctaLinkTarget?: "_self" | "_blank" | "_parent" | "_top";
  ctaText?: string;
  ctaAlign?: "left" | "center" | "right";
  ctaTextSize?: string;
  ctaTextMobileVariant?: string;
  displayCtaButton?: boolean;
  ctaButtonMobileVariant?: string;
  ctaTextDesktopVariant?: string;
  ctaButtonDesktopVariant?: string;
  ctaAriaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  bgImageSrc:
    "https://live-asuocms.ws.asu.edu/sites/default/files/2023-07/gray-background.gif",
  bgImageLazy: true,
  displayGradient: true,
  gradientOpacity: 0.85,
  clickableCard: false,
  title: "Lorem ipsum",
  titleSize: "xl",
  titleVariant: "white",
  titleAlign: "left",
  text: "Lorem ipsum dolor sit amet, consectetur adipisicing elit.",
  textSize: "xl",
  textVariant: "dark-3",
  textAlign: "left",
  displayCta: true,
  ctaLink: "https://asuonline.asu.edu",
  ctaLinkTarget: "_self",
  ctaText: "Learn more",
  ctaAlign: "left",
  ctaTextSize: "medium",
  ctaTextMobileVariant: "secondary",
  displayCtaButton: false,
  ctaButtonMobileVariant: "secondary",
  ctaTextDesktopVariant: "primary",
  ctaButtonDesktopVariant: "secondary",
});

const alignMap = {
  left: "start",
  center: "center",
  right: "end",
};

// translates basic alignment prop to flex box
const ctaAlignMap = {
  left: "start",
  center: "center",
  right: "end",
};
const cardTabIndex = computed(() => {
  return props.clickableCard ? "0" : "-1";
});
const cardClass = computed(() => {
  return props.clickableCard ? "card-focus" : null;
});
const cardStyle = computed(() => {
  const style: StyleValue = {
    cursor: props.clickableCard ? "pointer" : "default",
  };
  if (!props.bgImageLazy) {
    style["background-image"] = `url('${props.bgImageSrc}')`;
  }

  return style;
});
const dataBgImage = computed(() => {
  return props.bgImageLazy ? props.bgImageSrc : null;
});
const gradientStyle = computed(() => {
  if (props.displayGradient) {
    // mix gradient with gradientOpacity
    const color1 = "rgba(0,0,0,0)";
    const color2 =
      "rgba(0,0,0," + (0.745 * props.gradientOpacity).toString() + ")";
    const color3 = "rgba(0,0,0," + props.gradientOpacity.toString() + ")";

    return {
      background: `transparent linear-gradient(180deg, ${color1} 0%, ${color2} 28%, ${color3} 100%) 0% 0% no-repeat padding-box`,
    };
  } else {
    return null;
  }
});
const titleClass = computed(() => {
  return `h3-${props.titleSize} text-${props.titleVariant} text-${alignMap[props.titleAlign]}`;
});
const textClass = computed(() => {
  return `fs-${props.textSize} text-${props.textVariant} text-${alignMap[props.textAlign]}`;
});
const ctaParentClass = computed(() => {
  return `justify-content-${ctaAlignMap[props.ctaAlign]}`;
});
const ctaMobileClass = computed(() => {
  let classNames = `fs-${props.ctaTextSize}`;

  if (props.displayCtaButton) {
    classNames += ` btn btn-${props.ctaButtonMobileVariant}`;
  } else {
    classNames += ` text-underline text-${props.ctaTextMobileVariant}`;
  }

  return classNames;
});
const ctaDesktopClass = computed(() => {
  let classNames = `fs-${props.ctaTextSize}`;

  if (props.displayCtaButton) {
    classNames += ` btn btn-${props.ctaButtonDesktopVariant}`;
  } else {
    classNames += ` text-underline text-${props.ctaTextDesktopVariant}`;
  }

  return classNames;
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when any part of the card is clicked except for cta click.
   */
  (e: "cardClick", event: Event): void;
  /**
   *  Emitted when the cta is clicked, if present.
   */
  (e: "ctaClick", event: Event): void;
  /**
   *  Emitted when any the enter key is pressed while the card has focus and clickable-card is set to true.
   */
  (e: "cardEnter", event: Event): void;
}>();

const handleCardClick = (event: Event) => {
  emits("cardClick", event);
};

const handleCtaClick = (event: Event) => {
  event.stopPropagation();
  emits("ctaClick", event);
};

const handleCardEnter = (event: Event) => {
  if (props.clickableCard) {
    emits("cardEnter", event);
  }
};

// Add lozad on mount
onMounted(() => {
  if (typeof window !== "undefined" && window !== null && props.bgImageLazy) {
    const observer = lozad(".cit-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
}
.cit-card {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top;
  min-height: 350px;
}

.cit-front {
  bottom: 0px;
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .cit-card {
    min-height: 450px;
  }

  .cit-tile {
    opacity: 0;
    transition: opacity 0.5s linear;
  }

  .cit-card:hover .cit-tile,
  .cit-card:focus-within .cit-tile {
    opacity: 1;
  }

  .cit-front {
    min-height: 223px;
  }
}
</style>
